import { Box, Skeleton, Stack } from "@mui/material";

/**
 * Layout-preserving page skeletons.
 *
 * The provider app's navigation flicker (docs/archive/frontend-nav-followups.md) is a data-fetch gap:
 * a routed page mounts its shell with an empty body while its fetch is in flight, so the content
 * region collapses and re-expands. Rendering a skeleton with roughly the page's eventual footprint
 * — instead of nothing — turns that collapse→pop into a smooth skeleton→content transition.
 *
 * Prefer a `variant` that matches the destination's real layout. Data-dense pages that own a
 * distinctive layout should compose their own thin skeleton from these primitives so it tracks the
 * real footprint; generic pages can use <PageSkeleton /> directly.
 *
 * Modeled on the SkeletonRows pattern already in MtcList.tsx so the loading language stays
 * consistent across the app.
 */

export type PageSkeletonVariant = "detail" | "cards" | "table" | "form";

interface PageSkeletonProps {
  /** Footprint to mimic. Defaults to a generic detail page. */
  variant?: PageSkeletonVariant;
  /** Number of repeated units (table rows / cards / form fields). */
  count?: number;
  /** Hide the title/action header row (e.g. when the page renders its own header eagerly). */
  hideHeader?: boolean;
  /** Accessible label announced while loading. */
  label?: string;
}

// Shared cell divider, mirroring MtcList's CELL_BORDER so skeleton tables read the same.
const CELL_BORDER = { borderBottom: "1px solid", borderColor: "divider" } as const;

/** Title + action-button row most pages render at the top. */
const HeaderRow = () => (
  <Stack
    direction="row"
    sx={{ alignItems: "center", justifyContent: "space-between", mb: 3 }}
  >
    <Skeleton variant="text" width={220} sx={{ fontSize: "1.75rem" }} />
    <Stack direction="row" spacing={1}>
      <Skeleton variant="rounded" width={96} height={36} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 1 }} />
    </Stack>
  </Stack>
);

const TableBodySkeleton = ({ rows }: { rows: number }) => (
  <Box>
    {/* header strip */}
    <Stack direction="row" spacing={2} sx={{ ...CELL_BORDER, pb: 1.5, mb: 1 }}>
      {Array.from({ length: 5 }).map((_, c) => (
        <Skeleton
          key={`h-${c}`}
          variant="text"
          width={c === 0 ? "22%" : "16%"}
          sx={{ fontSize: "0.75rem" }}
        />
      ))}
    </Stack>
    {Array.from({ length: rows }).map((_, r) => (
      <Stack key={`r-${r}`} direction="row" spacing={2} sx={{ ...CELL_BORDER, py: 1.5 }}>
        {Array.from({ length: 5 }).map((_, c) => (
          <Skeleton
            key={`r-${r}-${c}`}
            variant="text"
            width={c === 0 ? "22%" : `${12 + ((r + c) % 4) * 4}%`}
            sx={{ fontSize: "0.875rem" }}
          />
        ))}
      </Stack>
    ))}
  </Box>
);

const CardsBody = ({ count }: { count: number }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    }}
  >
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={`card-${i}`}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" sx={{ fontSize: "0.75rem" }} />
          </Box>
        </Stack>
        <Skeleton variant="rounded" height={72} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
  </Box>
);

const FormBody = ({ count }: { count: number }) => (
  <Stack spacing={2.5} sx={{ maxWidth: 640 }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={`field-${i}`}>
        <Skeleton variant="text" width={140} sx={{ fontSize: "0.875rem", mb: 0.5 }} />
        <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
    <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 1 }} />
  </Stack>
);

const DetailBody = () => (
  <Stack spacing={2}>
    <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
    <Stack spacing={1}>
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="85%" />
      <Skeleton variant="text" width="55%" />
    </Stack>
    <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} />
  </Stack>
);

/**
 * A layout-preserving loading placeholder for a routed page. Fills its container width and holds
 * enough vertical space that the content region does not collapse while data is in flight.
 */
const PageSkeleton = ({
  variant = "detail",
  count,
  hideHeader = false,
  label = "Loading, please wait",
}: PageSkeletonProps) => {
  const units = count ?? (variant === "cards" ? 6 : variant === "form" ? 5 : 8);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
      // Width fills the routed slot; min-height keeps the region from collapsing to an empty shell.
      sx={{ width: "100%", minHeight: 320, p: { xs: 2, sm: 3 } }}
    >
      {!hideHeader && <HeaderRow />}
      {variant === "table" && <TableBodySkeleton rows={units} />}
      {variant === "cards" && <CardsBody count={units} />}
      {variant === "form" && <FormBody count={units} />}
      {variant === "detail" && <DetailBody />}
    </Box>
  );
};

export default PageSkeleton;
